import { figure, lotus, shield, slash, speed, sword } from '../kit';
import { C, mix } from '../pix';
import { defineIconKind, registerIcons } from '../store';

/**
 * General actions on the hotbar's top row: attack, guard (守, the combat triangle's block, marked
 * with the steel shield tab), jump, sprint, dash, sit and ride.
 */

export const ACTIONS = ['attack', 'guard', 'jump', 'sprint', 'dash', 'sit', 'ride'] as const;
export type ActionIcon = (typeof ACTIONS)[number];

const NAMES: Readonly<Record<ActionIcon, string>> = {
  attack: 'โจมตี',
  guard: 'ตั้งรับ',
  jump: 'กระโดด',
  sprint: 'วิ่งเร็ว',
  dash: 'พุ่งตัว',
  sit: 'นั่งสมาธิ',
  ride: 'ขี่พาหนะ',
};

const TINTS: Readonly<Record<ActionIcon, string>> = {
  attack: '#e8d8b0',
  guard: '#7ab8ff',
  jump: '#9ff0c8',
  sprint: '#9ff0c8',
  dash: '#9ff0c8',
  sit: '#ffd870',
  ride: '#7fd8ff',
};

const isAction = (id: string): id is ActionIcon => (ACTIONS as readonly string[]).includes(id);

defineIconKind('action', {
  label: 'ปุ่มคำสั่ง',
  ids: () => ACTIONS,
  name: (id) => (isAction(id) ? NAMES[id] : id),
  tint: (id) => (isAction(id) ? TINTS[id] : C.gold),
  plate: 'square',
  marker: (id) => (id === 'guard' ? 'guard' : 'none'),
  fallback: () => ({ draw: (p) => figure(p, 12, 10, C.white, 'stand') }),
});

const QI = C.qi;

registerIcons('action', {
  attack: (p) => {
    slash(p, 15, 17, 12, -2.5, 0.4, '#fff0c8', 3);
    sword(p, 5, 27, 26, 6, { hilt: C.gold });
  },
  guard: (p) => {
    shield(p, 18, 6, 18, 22, '#5a8ad8', '#c8dcf8');
    p.rect(17, 9, 2, 14, '#c8dcf8');
    p.rect(12, 13, 12, 2, '#c8dcf8');
  },
  jump: (p) => {
    for (let k = 0; k < 3; k++) {
      const y = 21 - k * 6;
      p.line(9, y + 4, 16, y, k ? mix(QI, C.ink, 0.3 * k) : QI).line(
        16,
        y,
        23,
        y + 4,
        k ? mix(QI, C.ink, 0.3 * k) : QI,
      );
    }
    figure(p, 13, 2, C.white, 'leap');
    p.ell(16, 28, 7, 1.5, mix(QI, C.ink, 0.5));
  },
  sprint: (p) => {
    speed(
      p,
      13,
      [
        [9, 8],
        [14, 10],
        [19, 7],
        [24, 9],
      ],
      QI,
    );
    figure(p, 14, 9, C.white, 'run');
    figure(p, 15, 9, C.white, 'run');
  },
  dash: (p) => {
    for (let k = 0; k < 3; k++)
      figure(p, 3 + k * 8, 10, k === 2 ? C.white : mix(QI, C.ink, 0.5 - k * 0.2), 'run');
    p.ring(24, 16, 8, 8, QI, -1.4, 1.4);
  },
  sit: (p) => {
    lotus(p, 16, 27, 22, '#ffd870', C.paper);
    figure(p, 13, 8, C.white, 'sit', { head: C.skin });
    p.ring(16, 12, 9, 9, '#fff0a8', -2.8, -0.3);
  },
  ride: (p) => {
    for (let x = 4; x < 26; x += 3) p.px(x, 25 + (x % 2), '#c8f0ff');
    sword(p, 3, 21, 29, 19, { hilt: C.gold, w: 2.6 });
    figure(p, 12, 8, C.white, 'stand', { head: C.skin });
  },
});
